import { Button } from "@workspace/ui/components/button";
import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetTrigger,
} from "@workspace/ui/components/sheet";
import { Skeleton } from "@workspace/ui/components/skeleton";
import { cn } from "@workspace/ui/lib/utils";
import Link from "next/link";
import { useRouter } from "next/router";
import type { ReactNode } from "react";
import { useEffect, useRef, useState } from "react";
import type { ExtendedUser } from "@/auth";
import { DashboardNavigation } from "@/components/dashboard-navigation";
import { LogoIcon } from "@/components/logo-icon";
import { OrganizationMenu } from "@/components/organization-menu";
import { useStableSession } from "@/lib/auth/use-stable-session";

export function DashboardSidebar() {
  const asideRef = useRef<HTMLElement>(null);
  const [hoverExpanded, setHoverExpanded] = useState(false);
  const [orgMenuOpen, setOrgMenuOpen] = useState(false);
  const expanded = hoverExpanded || orgMenuOpen;
  const { data: session, status } = useStableSession();
  const isEmployer =
    status === "authenticated" &&
    (session?.user as ExtendedUser | undefined)?.type === "employer";

  const syncExpandedWithPointer = () => {
    if (!asideRef.current?.matches(":hover")) {
      setHoverExpanded(false);
    }
  };

  const handleOrgMenuOpenChange = (open: boolean) => {
    setOrgMenuOpen(open);
    if (!open) {
      syncExpandedWithPointer();
    }
  };

  return (
    // biome-ignore lint/a11y/noNoninteractiveElementInteractions: hover only reveals labels; collapsed links remain labelled and keyboard operable.
    <aside
      aria-label="Sidebar"
      className={cn(
        "fixed top-0 left-0 z-50 hidden h-svh flex-col border-r bg-background transition-all duration-200 ease-[cubic-bezier(0.4,0,0.2,1)] motion-reduce:transition-none md:flex",
        expanded ? "w-[240px] shadow-none" : "w-[70px] shadow-none"
      )}
      onMouseEnter={() => setHoverExpanded(true)}
      onMouseLeave={() => setHoverExpanded(false)}
      ref={asideRef}
    >
      <div className="flex h-[70px] shrink-0 items-center border-b px-[19px]">
        <Link
          aria-label="VacWork home"
          className="flex size-8 items-center justify-center"
          href="/"
        >
          <LogoIcon />
        </Link>
      </div>
      <nav
        aria-label="Main navigation"
        className={cn(
          "flex flex-1 flex-col overflow-hidden px-[15px] pt-4 pb-3",
          isEmployer && "border-b"
        )}
      >
        {status === "loading" ? (
          <div className="flex flex-col gap-2">
            {[0, 1, 2, 3].map((item) => (
              <Skeleton className="size-10" key={item} />
            ))}
          </div>
        ) : (
          <DashboardNavigation expanded={expanded} />
        )}
      </nav>
      <div className="relative flex h-14 shrink-0 items-center pl-[19px]">
        {status === "authenticated" && (
          <OrganizationMenu
            isExpanded={expanded}
            onOpenChange={handleOrgMenuOpenChange}
          />
        )}
      </div>
    </aside>
  );
}

export function DashboardMobileMenu({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const { data: session, status } = useStableSession();
  const router = useRouter();

  useEffect(() => {
    const dismiss = () => setOpen(false);
    router.events.on("routeChangeStart", dismiss);
    return () => {
      router.events.off("routeChangeStart", dismiss);
    };
  }, [router]);

  const isEmployer =
    status === "authenticated" &&
    (session?.user as ExtendedUser | undefined)?.type === "employer";

  return (
    <Sheet onOpenChange={setOpen} open={open}>
      <SheetTrigger asChild>
        <Button
          aria-label="Open navigation"
          className="size-8 rounded-full md:hidden"
          size="icon"
          variant="outline"
        >
          {children}
        </Button>
      </SheetTrigger>
      <SheetContent
        className="w-[260px] gap-0 p-0 shadow-none sm:max-w-[260px]"
        side="left"
      >
        <SheetTitle className="sr-only">Navigation</SheetTitle>
        <div className="flex h-[70px] shrink-0 items-center border-b px-5">
          <Link
            aria-label="VacWork home"
            className="flex size-8 items-center justify-center"
            href="/"
            onClick={() => setOpen(false)}
          >
            <LogoIcon />
          </Link>
        </div>
        <nav
          aria-label="Mobile navigation"
          className={cn(
            "flex flex-1 flex-col px-[15px] pt-4 pb-3",
            isEmployer && "border-b"
          )}
        >
          {status === "loading" ? (
            <div className="flex flex-col gap-2">
              {[0, 1, 2, 3].map((item) => (
                <Skeleton className="h-10 w-full" key={item} />
              ))}
            </div>
          ) : (
            <DashboardNavigation
              expanded={true}
              onNavigate={() => setOpen(false)}
            />
          )}
        </nav>
        <div className="flex h-[60px] shrink-0 items-center px-5">
          {status === "authenticated" && <OrganizationMenu isExpanded={true} />}
        </div>
      </SheetContent>
    </Sheet>
  );
}
